<template>
  <v-card outlined class="relatorio-adesao">
    <v-toolbar flat dense color="primary" dark>
      <v-icon left>mdi-pill</v-icon>
      <v-toolbar-title>Adesão ao tratamento</v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>

    <!-- Sem permissão AdesaoTratamentoRead: não tenta carregar (o backend negaria de qualquer forma) -->
    <v-card-text v-if="!temPermissao" class="text-center grey--text">
      <v-icon class="mr-2">mdi-lock</v-icon>
      Você não tem permissão para ver o relatório de adesão.
    </v-card-text>

    <v-card-text v-else>
      <!-- Filtros de período -->
      <v-row dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filtroDe"
            label="De"
            type="date"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filtroAte"
            label="Até"
            type="date"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-btn color="primary" block :loading="carregando" @click="carregar">
            <v-icon left>mdi-magnify</v-icon>
            Aplicar
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-3"></v-divider>

      <v-progress-circular v-if="carregando" indeterminate color="primary" class="d-block mx-auto my-6" />

      <template v-else-if="relatorio">
        <v-card-text v-if="relatorio.dosesEsperadas === 0" class="text-center grey--text">
          Nenhuma dose esperada no período (paciente sem guia de medicamento ativa).
        </v-card-text>

        <template v-else>
          <!-- Adesão total -->
          <v-row dense>
            <v-col cols="12" md="4">
              <v-alert :color="corPercentual(relatorio.percentualAdesao)" text prominent border="left" class="mb-0">
                <div class="text-h3 font-weight-bold">{{ relatorio.percentualAdesao }}%</div>
                <div class="text-caption">
                  Adesão total — {{ relatorio.dosesConfirmadas }} de {{ relatorio.dosesEsperadas }} dose(s)
                </div>
              </v-alert>
            </v-col>
            <v-col cols="12" md="8" class="d-flex align-center">
              <v-row dense class="text-center">
                <v-col cols="6">
                  <div class="text-h5 font-weight-bold success--text">{{ relatorio.dosesConfirmadas }}</div>
                  <div class="text-caption">Doses confirmadas</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-h5 font-weight-bold grey--text">{{ relatorio.dosesEsperadas }}</div>
                  <div class="text-caption">Doses esperadas no período</div>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- % por medicamento (gráfico de barras + tabela) -->
          <div class="text-subtitle-2 mt-4 mb-1">Por medicamento</div>
          <div
            v-for="medicamento in relatorio.porMedicamento"
            :key="medicamento.guiaMedicamentoItemId"
            class="d-flex align-center mb-1"
          >
            <span class="mr-2 text-caption font-weight-medium nome-medicamento">
              {{ medicamento.medicamento }}
            </span>
            <v-progress-linear
              :value="medicamento.percentualAdesao"
              :color="corPercentual(medicamento.percentualAdesao)"
              height="14"
              rounded
              class="flex-grow-1"
            />
            <span class="ml-2 text-caption percentual-medicamento">
              {{ medicamento.percentualAdesao }}%
            </span>
          </div>

          <v-simple-table dense class="mt-3">
            <template #default>
              <thead>
                <tr>
                  <th class="text-left">Medicamento</th>
                  <th class="text-right">Esperadas</th>
                  <th class="text-right">Confirmadas</th>
                  <th class="text-right">Adesão</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="medicamento in relatorio.porMedicamento" :key="`linha-${medicamento.guiaMedicamentoItemId}`">
                  <td>{{ medicamento.medicamento }}</td>
                  <td class="text-right">{{ medicamento.dosesEsperadas }}</td>
                  <td class="text-right">{{ medicamento.dosesConfirmadas }}</td>
                  <td class="text-right font-weight-bold" :class="`${corPercentual(medicamento.percentualAdesao)}--text`">
                    {{ medicamento.percentualAdesao }}%
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </template>
      </template>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import Vue from 'vue';
import PermissoesPadrao from '@/constants/enums/PermissoesPadrao';
import { AdesaoService, AdesaoRelatorio } from '@/services/AdesaoService';
import { MensagemMixin } from '@/helpers/MensagemHelper';
import { decode, getToken } from '@/utils/Token';

// C3 — Relatório de adesão ao tratamento do paciente (staff, permission
// AdesaoTratamentoRead no backend). % de doses confirmadas vs esperadas
// (horários da guia ativa × dias do período), por medicamento e total.
export default Vue.extend({
  name: 'soi-relatorio-adesao',
  props: {
    pacienteId: {
      type: Number,
      required: true,
    },
  },
  data() {
    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 29);
    return {
      service: new AdesaoService(),
      temPermissao: true,
      relatorio: null as AdesaoRelatorio | null,
      filtroDe: trintaDiasAtras.toISOString().substring(0, 10),
      filtroAte: hoje.toISOString().substring(0, 10),
      carregando: false,
    };
  },
  created() {
    // Mesmo padrão de gating dos demais componentes (claim de permissions do JWT).
    const sessaoUsuario = decode(getToken());
    this.temPermissao = !!sessaoUsuario?.permissions?.includes(PermissoesPadrao.AdesaoTratamentoRead);
    if (this.temPermissao) {
      this.carregar();
    }
  },
  watch: {
    pacienteId() {
      if (this.temPermissao) {
        this.carregar();
      }
    },
  },
  methods: {
    async carregar() {
      this.carregando = true;
      try {
        const res = await this.service.Relatorio(
          this.pacienteId,
          this.filtroDe || undefined,
          this.filtroAte || undefined,
        );
        this.relatorio = res.data;
      } catch (err) {
        MensagemMixin(3000, 'error', 'Não foi possível carregar o relatório de adesão.');
      } finally {
        this.carregando = false;
      }
    },

    corPercentual(percentual: number): string {
      if (percentual >= 80) return 'success';
      if (percentual >= 50) return 'warning';
      return 'error';
    },
  },
});
</script>

<style scoped>
.relatorio-adesao {
  width: 100%;
}
.nome-medicamento {
  width: 160px;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.percentual-medicamento {
  width: 48px;
  text-align: right;
}
</style>
